// src/App.tsx
import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "./theme/theme";
import ErrorBoundary from "./components/ErrorBoundary";
import GtagRouteTracker from "./components/GtagRouteTracker";

// Lazy load page components for code splitting
const GameListPage = React.lazy(() => import("./pages/games/gameListPage"));
const GameFormPage = React.lazy(() => import("./pages/games/gameFormPage"));
const EditScreenPage = React.lazy(() => import("./pages/games/EditSequenceElementPage"));
const CountryPage = React.lazy(() => import("./pages/admin/Country/CountryPage"));
const CityPage = React.lazy(() => import("./pages/admin/Country/CityPage"));
const LocalizationPage = React.lazy(() => import("./pages/admin/Country/LocalizationPage"));
const LanguagePage = React.lazy(() => import("./pages/admin/Country/LanguagePage"));
const IndexPage = React.lazy(() => import("./pages/public/indexPage"));
const HowItWorksPage = React.lazy(() => import("./pages/public/HowItWorksPage"));
const LanguageRedirect = React.lazy(() => import("./components/LanguageRedirect"));
const HowItWorksLanguageRedirect = React.lazy(() => import("./components/HowItWorksLanguageRedirect"));
// Language selector will be code-split and only loaded on demand
const LanguageSelector = React.lazy(() => import("./components/LanguageSelector"));
const AdminIndexPage = React.lazy(() => import("./pages/admin/AdminIndexPage"));
const About = React.lazy(() => import("./pages/public/about"));
const WhatWeDo = React.lazy(() => import("./pages/public/whatWeDo"));
const PolicyPage = React.lazy(() => import("./pages/public/PolicyPage"));
const InternalTestLinksPage = React.lazy(async () => {
  const module = await import("./pages/public/TestLinksPage");
  return {
    default: function InternalTestLinksRoute() {
      return <module.TestLinksPage variant="internal" />;
    },
  };
});
const ExternalTestLinksPage = React.lazy(async () => {
  const module = await import("./pages/public/TestLinksPage");
  return {
    default: function ExternalTestLinksRoute() {
      return <module.TestLinksPage variant="external" />;
    },
  };
});
const BetaTestLinksPage = React.lazy(async () => {
  const module = await import("./pages/public/TestLinksPage");
  return {
    default: function BetaTestLinksRoute() {
      return <module.TestLinksPage variant="beta" />;
    },
  };
});
const AuthActionPage = React.lazy(() => import("./pages/public/AuthActionPage"));
const DeleteAccountPage = React.lazy(() => import("./pages/public/DeleteAccountPage"));
const LoginPage = React.lazy(() => import("./pages/admin/administration/loginPage"));
const UserPage = React.lazy(() => import("./pages/admin/administration/userPage"));
const SetPasswordPage = React.lazy(() => import("./pages/admin/administration/SetPasswordPage"));
const AdminPolicyPage = React.lazy(() => import("./pages/admin/PolicyPage"));
const AdminTestLinksPage = React.lazy(() => import("./pages/admin/TestLinksPage"));
const AppLocalStringsPage = React.lazy(() => import("./pages/admin/AppLocalStringsPage"));
const GameJointsPage = React.lazy(() => import("./pages/admin/GameJointsPage"));
const IssueReportsPage = React.lazy(() => import("./pages/admin/IssueReportsPage"));
const PartnersPage = React.lazy(() => import("./pages/admin/PartnersPage"));
const PartnerTrackingPage = React.lazy(() => import("./pages/admin/PartnerTrackingPage"));
const BookingCodesPage = React.lazy(() => import("./pages/admin/BookingCodesPage"));
const QrLandingPage = React.lazy(() => import("./pages/public/QrLandingPage"));

// Lazy loaded admin shell that wraps protected routes (auth + sidenav) to keep admin-only code out of initial bundle
const AdminShell = React.lazy(() => import("./pages/admin/AdminShell"));

// Loading component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

export default function App() {
  // Idle prefetch: eagerly (but low priority) warm admin shell & language selector after first paint
  useEffect(() => {
    // requestIdleCallback polyfill
    const ric: typeof requestIdleCallback = (window as any).requestIdleCallback ||
      function (cb: any) { return setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 150); } as any;
    ric(() => {
      import(/* webpackPrefetch: true */ './pages/admin/AdminShell');
      import(/* webpackPrefetch: true */ './components/LanguageSelector');
    });
  }, []);

  return (
    <ThemeProvider theme={appTheme}>
      <BrowserRouter>
        {/* Google Ads page views for client-side route changes */}
        <GtagRouteTracker />
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
          {/* Root route - redirects to language-specific route based on IP */}
          <Route path="/" element={<LanguageRedirect />} />
          
          {/* How it works page: language LAST, per product spec
              (touristgames.com/howitworks/en). "/howitworks" with no language
              detects the visitor's country by IP and redirects to a locale.
              Declared before "/:lang" so the static segment wins in v6. */}
          <Route path="/howitworks" element={<HowItWorksLanguageRedirect />} />
          <Route path="/howitworks/:lang" element={<HowItWorksPage />} />

          {/* Language-specific public routes */}
          <Route path="/:lang" element={<IndexPage />} />
          <Route path="/:lang/about" element={<About />} />
          <Route path="/:lang/what-we-do" element={<WhatWeDo />} />
          <Route path="/:lang/policies/:slug" element={<PolicyPage />} />
          <Route path="/:lang/internal-test-links" element={<InternalTestLinksPage />} />
          <Route path="/:lang/external-test-links" element={<ExternalTestLinksPage />} />
          <Route path="/:lang/beta-test-links" element={<BetaTestLinksPage />} />
          
          {/* Non-language specific routes */}
          {/* Public QR landing page (one QR code per partner/hotel) */}
          <Route path="/qr/:partnerId" element={<QrLandingPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/login" element={<LoginPage />} />
          

          {/* Admin routes (lazy-loaded shell: auth + sidenav) */}
          <Route element={<AdminShell />}>        
            <Route path="/admin" element={<AdminIndexPage />} />
            <Route path="/admin/Country" element={<CountryPage />} />
            <Route path="/admin/localization" element={<LocalizationPage />} />
            <Route path="/admin/app-local-strings" element={<AppLocalStringsPage />} />
            <Route path="/admin/policies" element={<AdminPolicyPage />} />
            <Route path="/admin/test-links" element={<AdminTestLinksPage />} />
            <Route path="/admin/games" element={<GameListPage />} />
            <Route path="/admin/game-joints" element={<GameJointsPage />} />
            <Route path="/admin/games/new" element={<GameFormPage />} />
            <Route path="/admin/languages" element={<LanguagePage />} />
            <Route path="/admin/games/:id" element={<GameFormPage />} />
            <Route path="/admin/games/:gameId/editScreenPage/:id" element={<EditScreenPage />} />
            <Route path="/admin/games/:gameId/EditScreenPage/:id" element={<EditScreenPage />} />
            <Route path="/admin/cities/:countryId" element={<CityPage />} />
            <Route path="/admin/users" element={<UserPage />} />
            <Route path="/admin/issue-reports" element={<IssueReportsPage />} />
            <Route path="/admin/partners" element={<PartnersPage />} />
            <Route path="/admin/partner-tracking" element={<PartnerTrackingPage />} />
            <Route path="/admin/booking-codes" element={<BookingCodesPage />} />
          </Route>
          {/* Accessible after authentication to set initial password */}
          <Route path="/admin/set-password" element={<SetPasswordPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}
